import { useMemo } from "react";
import "./BookingAvailabilityCalendar.css";

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function parseIso(iso) {
  const m = String(iso || "").match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;
  return { year: Number(m[1]), month: Number(m[2]), day: Number(m[3]), iso: `${m[1]}-${m[2]}-${m[3]}` };
}

function addDaysIso(iso, delta) {
  const p = parseIso(iso);
  if (!p) return null;
  const d = new Date(Date.UTC(p.year, p.month - 1, p.day + delta));
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, "0");
  const day = String(d.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function weekdaySundayFirst(iso) {
  const p = parseIso(iso);
  if (!p) return 0;
  return new Date(p.year, p.month - 1, p.day).getDay();
}

function formatRangeTitle(minDate, maxDate) {
  const a = parseIso(minDate);
  const b = parseIso(maxDate);
  if (!a || !b) return "Availability";
  const fmt = (p) =>
    new Date(p.year, p.month - 1, p.day).toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
    });
  if (a.year === b.year && a.month === b.month) {
    return new Date(a.year, a.month - 1, 1).toLocaleDateString("en-IN", {
      month: "long",
      year: "numeric",
    });
  }
  return `${fmt(a)} – ${fmt(b)}`;
}

function reasonLabel(entry) {
  if (!entry) return "Outside booking window";
  if (entry.available) return "Available";
  const code = String(entry.reason || "").toUpperCase();
  if (entry.onLeave || code === "ON_LEAVE" || code === "CAREGIVER_BLOCKED_ON_DATE") {
    return "Leave / blocked";
  }
  if (code === "BOOKING_DATE_PAST") return "Past date";
  if (code === "BOOKING_DATE_OUTSIDE_WINDOW" || code === "BOOKING_DATE_OUT_OF_RANGE") {
    return "Outside booking window";
  }
  if (code === "NO_WORKING_HOURS") return "Not rostered";
  if (code === "CAREGIVER_ENGAGED" || code === "EMERGENCY_PAUSED") return "Unavailable";
  if (code === "NO_SLOTS" || code === "LIVE_IN_UNAVAILABLE" || code === "FULLY_BOOKED") {
    return "No open slots";
  }
  return entry.message || "Unavailable";
}

/**
 * Customer-facing date picker that greys out unavailable caregiver dates.
 * Renders every day in [minDate, maxDate] (booking window) — native date inputs
 * cannot disable individual days.
 */
export default function BookingAvailabilityCalendar({
  minDate,
  maxDate,
  value = "",
  selectedDates = null,
  multi = false,
  daysByDate = {},
  loading = false,
  error = "",
  disabled = false,
  onSelect,
  onRetry,
  ariaLabel = "Select booking date",
}) {
  const selectedSet = useMemo(() => {
    if (multi && Array.isArray(selectedDates)) {
      return new Set(selectedDates.map((d) => String(d).slice(0, 10)));
    }
    const single = String(value || "").slice(0, 10);
    return single ? new Set([single]) : new Set();
  }, [multi, selectedDates, value]);

  const cells = useMemo(() => {
    if (!minDate || !maxDate || minDate > maxDate) return [];
    const out = [];
    const lead = weekdaySundayFirst(minDate);
    for (let i = 0; i < lead; i += 1) {
      out.push({ key: `pad-start-${i}`, empty: true });
    }
    let cursor = minDate;
    while (cursor && cursor <= maxDate) {
      const p = parseIso(cursor);
      const entry = daysByDate[cursor] || null;
      const available = Boolean(entry?.available);
      const selectable = !disabled && !loading && !error && available;
      out.push({
        key: cursor,
        empty: false,
        iso: cursor,
        day: p?.day,
        monthShort: p
          ? new Date(p.year, p.month - 1, 1).toLocaleDateString("en-IN", { month: "short" })
          : "",
        available,
        selectable,
        selected: selectedSet.has(cursor),
        entry,
        title: loading
          ? "Loading availability…"
          : error
            ? "Availability unavailable"
            : reasonLabel(entry),
      });
      cursor = addDaysIso(cursor, 1);
    }
    return out;
  }, [minDate, maxDate, daysByDate, disabled, loading, error, selectedSet]);

  function handlePick(cell) {
    if (!cell?.selectable || !onSelect) return;
    onSelect(cell.iso);
  }

  return (
    <div className="bac" role="group" aria-label={ariaLabel} aria-busy={loading || undefined}>
      <div className="bac__head">
        <h4 className="bac__month">{formatRangeTitle(minDate, maxDate)}</h4>
        {loading ? <span className="bac__status">Checking availability…</span> : null}
        {!loading && error ? (
          <span className="bac__status bac__status--error" role="alert">
            {error}
            {onRetry ? (
              <button type="button" className="bac__retry" onClick={onRetry}>
                Retry
              </button>
            ) : null}
          </span>
        ) : null}
        {!loading && !error ? (
          <span className="bac__status bac__status--ok">Greyed dates are unavailable</span>
        ) : null}
      </div>

      <div className="bac__weekdays" aria-hidden="true">
        {WEEKDAYS.map((label) => (
          <span key={label} className="bac__weekday">
            {label}
          </span>
        ))}
      </div>

      <div className="bac__grid">
        {cells.map((cell) => {
          if (cell.empty) {
            return <span key={cell.key} className="bac__cell bac__cell--empty" />;
          }
          const classes = [
            "bac__cell",
            cell.selected ? "is-selected" : "",
            cell.selectable ? "is-available" : "is-disabled",
            !cell.available ? "is-unavailable" : "",
          ]
            .filter(Boolean)
            .join(" ");
          return (
            <button
              key={cell.key}
              type="button"
              className={classes}
              disabled={!cell.selectable}
              title={cell.title}
              aria-label={`${cell.iso}${cell.selectable ? "" : ` — ${cell.title}`}`}
              aria-pressed={cell.selected}
              onClick={() => handlePick(cell)}
            >
              <span className="bac__day-num">{cell.day}</span>
              <span className="bac__day-mon">{cell.monthShort}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
